/* eslint-disable no-bitwise */
import { FFT } from './fft';
import { MDCT_TABLE_2048, MDCT_TABLE_256, MDCT_TABLE_1920, MDCT_TABLE_240 } from './mdct_tables';

/**
 * Modified Discrete Cosine Transform
 */
export class MDCT {

  private N2: number;
  private N4: number;
  private N8: number;

  private sincos: Float32Array;
  private fft: FFT;
  private buf: Float32Array;
  private tmp = new Float32Array(2);

  constructor(length: number) {
    switch (length) {
      case 2048: this.sincos = MDCT_TABLE_2048; break;
      case 256: this.sincos = MDCT_TABLE_256; break;
      case 1920: this.sincos = MDCT_TABLE_1920; break;
      case 240: this.sincos = MDCT_TABLE_240; break;
      default: throw new Error(`Unsupported MDCT length: ${length}`);
    }

    this.N2 = length >>> 1;
    this.N4 = length >>> 2;
    this.N8 = length >>> 3;

    this.fft = new FFT(this.N4);

    this.buf = new Float32Array(length);
  }


  process(input: Float32Array, inOffset: number, output: Float32Array, outOffset: number) {
    // local access
    const N2 = this.N2;
    const N4 = this.N4;
    const buf = this.buf;
    const tmp = this.tmp;
    const sincos = this.sincos;
    const fft = this.fft;

    // pre-IFFT complex multiplication
    for (let k = 0; k < N4; k++) {
      buf[2 * k + 1] = (input[inOffset + 2 * k] * sincos[2 * k]) + (input[inOffset + N2 - 1 - 2 * k] * sincos[2 * k + 1]);
      buf[2 * k] = (input[inOffset + N2 - 1 - 2 * k] * sincos[2 * k]) - (input[inOffset + 2 * k] * sincos[2 * k + 1]);
    }

    // complex IFFT, non-scaling
    fft.process(buf, false);

    // post-IFFT complex multiplication
    for (let k = 0; k < N4; k++) {
      tmp[0] = buf[2 * k];
      tmp[1] = buf[2 * k + 1];
      buf[2 * k + 1] = (tmp[1] * sincos[2 * k]) + (tmp[0] * sincos[2 * k + 1]);
      buf[2 * k] = (tmp[0] * sincos[2 * k]) - (tmp[1] * sincos[2 * k + 1]);
    }

    // First, let's adjust our values to be in the same -1;1 range as ffmpeg
    for (let i = 0; i < buf.length; i++) {

      buf[i] /= 32768;
    }

    // Then, let's move re-arrange the values to have them exactly as ffmpeg,
    // which includes inverting the sign of the buf[0] ones
    this.reArrange(buf, output, outOffset);
  }


  private reArrange(buf: Float32Array, output: Float32Array, outOffset: number) {
    const N2 = this.N2;
    const N4 = this.N4;
    const N8 = this.N8;

    for (let i = 0; i < N8; i++) {
      const j0 = i;
      const k0 = i * 2;
      output[outOffset + k0] = -buf[2 * j0];

      const j1 = i;
      const k1 = N2 - 1 - i * 2;
      output[outOffset + k1] = buf[2 * j1 + 1];

      const j2 = N8 + i;
      const k2 = 2 * j2;
      output[outOffset + k2] = -buf[2 * j2];

      const j3 = N8 + i;
      const k3 = N4 - 1 - 2 * i;
      output[outOffset + k3] = buf[2 * j3 + 1];
    }
  }

}


